import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import Footer from '@Components/Footer';
import useDeleteFocusManager from '@Hooks/useDeleteFocusManager';
import { buildAlertDeleteButtonId } from '@Pages/Alerts/AlertsUtils';
import AlertDeleteConfirmationModal from '@Pages/Alerts/AlertsComponents/AlertDeleteConfirmationModal/AlertDeleteConfirmationModal';
import NoAlerts from './AlertsComponents/NoAlerts';
import GroupsList from './AlertsComponents/GroupsList';

import './Alerts.scss';

// Alerts icon in the header, used as a fallback focus target when the deleted item is not in the list anymore (e.g. when deleting the last alert in a group)
const FALLBACK_FOCUS_ID = 'alerts-icon';

export default function Alerts({
  groupsIds,
  renderedAlertsIds,
  showDeleteModal,
  alertIdToDelete,
  handleViewDeleteModal,
  removeAlert,
  isDeleteInProgress,
  deletingAlertId,
}) {
  const result = groupsIds && (groupsIds.count() ? <GroupsList groupsIds={groupsIds} /> : <NoAlerts />);

  const deleteButtonIds = useMemo(() => {
    if (!renderedAlertsIds) return [];

    const alertIds = renderedAlertsIds.toJS();

    return alertIds.map(buildAlertDeleteButtonId);
  }, [renderedAlertsIds]);

  const deletingId = deletingAlertId ? buildAlertDeleteButtonId(deletingAlertId) : null;

  useDeleteFocusManager({
    itemIds: deleteButtonIds,
    deletingId,
    isDeleting: !!isDeleteInProgress,
    fallbackFocusId: FALLBACK_FOCUS_ID,
  });

  return (
    <div className="alerts-page__container alerts-container">
      {showDeleteModal && (
        <AlertDeleteConfirmationModal
          onCancel={() => handleViewDeleteModal(false)}
          onConfirm={() => {
            removeAlert(alertIdToDelete);
            handleViewDeleteModal(false);
          }}
        />
      )}
      <div id="main-content" className="alerts-content" aria-labelledby="alerts-title">
        <section className="rx-list-wrapper">
          <div className="header-container--border-bottom u-els-padding-top-1x u-els-padding-bottom-1x">
            <h1 id="alerts-title" tabIndex="-1" className="alerts-heading u-els-font-size-h3 rx-left-gutter">
              Alerts
            </h1>
          </div>
          <div className="alerts-items-wrapper rx-list-wrapper rx-left-gutter rx-right-gutter">{result}</div>
        </section>
      </div>
      <Footer />
    </div>
  );
}

Alerts.propTypes = {
  groupsIds: PropTypes.instanceOf(List),
  renderedAlertsIds: PropTypes.instanceOf(List),
  showDeleteModal: PropTypes.bool,
  alertIdToDelete: PropTypes.string,
  handleViewDeleteModal: PropTypes.func,
  removeAlert: PropTypes.func,
  isDeleteInProgress: PropTypes.bool,
  deletingAlertId: PropTypes.string,
};
